import type { Account } from '@shared/products-types';
import { getFeatureConfig } from '@luna/feature-config';
import type { Amount } from '@shared/core-types';
import type { ProductWithHelpers } from '@shared/products-core';
import type { Feature } from '@luna/gatekeeper';
import { isFeatureEnabled } from '@luna/gatekeeper';

const DISPOSABLE = 'disposable';

const shouldDisplayDisposable = (): boolean =>
    getFeatureConfig((f) => f.savings.useDisposableInsteadOfBalance);
const isUsingDisposable = (): boolean =>
    getFeatureConfig((config) => config.base.mainProductAmount) === DISPOSABLE;

const isUsingOwnResources = (): boolean =>
    isFeatureEnabled<{ accounts: { ownResources: Feature } }>((f) => f.accounts.ownResources);

export const getAccountAmount = (account: ProductWithHelpers<Account>): Amount | undefined => {
    if (account.type === 'LOAN' && account.subtype === 'REVOLVING_LOAN') {
        return account.loan?.remainingLoanAmount;
    }

    if (account.type === 'LOAN' && !!getFeatureConfig((config) => config.base.loanProductAmount)) {
        const amountFieldNameFromFeatureConfig = getFeatureConfig(
            (config) => config.base.loanProductAmount,
        )!;

        return account[amountFieldNameFromFeatureConfig as keyof Account];
    }

    if (isUsingOwnResources() && account?.ownResources && account.type === 'CURRENT') {
        return account.ownResources;
    }

    if (
        isUsingDisposable() &&
        account.disposable &&
        (account.type !== 'SAVING' || shouldDisplayDisposable())
    ) {
        return account.disposable;
    }

    return account.balance;
};

export const getAccountAmountLocalCurrency = (
    account: ProductWithHelpers<Account>,
): Amount | undefined => {
    if (
        account.type === 'LOAN' &&
        getFeatureConfig((config) => config.base.loanProductAmount) === 'balance'
    ) {
        return account.balanceLocalCurrency;
    }

    if (isUsingOwnResources() && account?.ownResourcesLocalCurrency && account.type === 'CURRENT') {
        return account.ownResourcesLocalCurrency;
    }

    if (
        isUsingDisposable() &&
        account.disposable &&
        (account.type !== 'SAVING' || shouldDisplayDisposable())
    ) {
        return account.disposableLocalCurrency;
    }

    return account.balanceLocalCurrency;
};
